<template>
  <div
    class="fixed inset-0 z-[12345678900] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 transition-opacity"
    @click="toggleModal"
  >
    <div
      class="bg-white w-full max-w-4xl max-h-[95vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden transition-all transform"
      @click.stop
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-slate-100"
      >
        <div class="flex items-center gap-2">
          <div class="p-2.5 bg-slate-50 rounded-xl ring-1 ring-slate-100">
            <img
              width="22"
              height="22"
              src="/svg/rain.svg"
              class="opacity-70 filter invert-[1]"
            />
          </div>
          <h2 class="text-xl font-bold text-slate-800 tracking-tight">
            Rain Time Intervals
          </h2>
        </div>
        <button
          @click="toggleModal"
          class="p-2 rounded-full hover:bg-slate-50 text-slate-400 transition-colors"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-4 py-3 space-y-5">
        <div
          v-if="!isRainSlotSaving && !isRainNotGenerable"
          class="grid grid-cols-1 gap-3"
        >
          <div
            class="flex gap-3 p-1.5 bg-amber-50/50 border border-amber-100 rounded-xl"
          >
            <i
              class="bi bi-exclamation-triangle-fill text-amber-500 text-lg"
            ></i>
            <p class="text-[12px] text-amber-800 leading-snug">
              Today's rains are <b>non-editable</b>. New generations will update
              only unused future slots.
            </p>
          </div>
          <div
            class="flex gap-3 p-1.5 bg-blue-50/50 border border-blue-100 rounded-xl"
          >
            <i class="bi bi-info-circle-fill text-blue-500 text-lg"></i>
            <p class="text-[12px] text-blue-800 leading-snug">
              The "End At" time is automatically calculated based on the "Start
              At" time, by adding the predefined rain duration. The latest
              possible end time is 23:59. If a rain interval would end exactly
              at 23:59, the corresponding start time will be adjusted backward
              to ensure the interval still respects the defined duration.
            </p>
          </div>
        </div>

        <div
          class="flex items-center justify-between p-1.5 bg-slate-50/80 border border-slate-100 rounded-2xl"
        >
          <div class="flex items-center gap-2">
            <label class="relative flex items-center cursor-pointer">
              <input type="checkbox" v-model="isRandom" class="peer sr-only" />
              <div
                class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#386f99]"
              ></div>
            </label>
            <div>
              <span class="block text-sm font-bold text-slate-700"
                >Random Mode</span
              >
              <span class="text-[11px] text-slate-500"
                >Each day will have different, unpredictable rain times.</span
              >
            </div>
          </div>
          <div
            v-if="isRandom"
            class="px-3 py-1 bg-blue-100 rounded-lg animate-pulse"
          >
            <span
              class="text-[10px] font-bold text-blue-700 uppercase tracking-widest"
              >Active</span
            >
          </div>
        </div>

        <div
          v-if="!isRainSlotSaving && !isRainNotGenerable && !isRandom"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-3.5"
        >
          <div
            v-for="(interval, index) in localIntervals"
            :key="index"
            :class="[
              'relative p-3 rounded-xl border transition-all duration-300',
              editMode
                ? interval.start_at
                  ? 'bg-white !border-blue-200 !shadow-lg'
                  : 'bg-red-50 !border-red-300 shadow-none'
                : 'bg-white border-slate-100 shadow-sm hover:shadow-md',
            ]"
          >
            <div
              class="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-800 rounded-full flex items-center justify-center"
            >
              <span
                class="text-[10px] font-bold text-white uppercase tracking-tighter"
                >Rain {{ index + 1 }}</span
              >
            </div>

            <div class="mt-2 flex items-center justify-between gap-3">
              <div class="flex-1">
                <label
                  class="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1"
                  >Start</label
                >
                <div class="relative">
                  <input
                    v-if="editMode"
                    type="time"
                    v-model="interval.start_at"
                    @input="validateTime(index)"
                    class="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                  <div
                    v-else
                    class="px-3 py-2 bg-slate-50/50 rounded-xl text-lg font-mono font-bold text-slate-700 border border-transparent"
                  >
                    {{ interval.start_at }}
                  </div>
                </div>
              </div>

              <div class="my-0 mx-auto relative top-[10px] text-slate-300">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </div>

              <div class="flex-1">
                <label
                  class="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1 text-right"
                >
                  End
                </label>
                <div
                  :class="[
                    'px-3 py-2 rounded-xl text-lg font-mono font-bold text-center border',
                    editMode
                      ? 'bg-blue-50 border-blue-100 text-blue-600'
                      : 'bg-slate-50/50 border-transparent text-slate-400',
                  ]"
                >
                  {{
                    interval.end_at && !interval.end_at.includes("NaN")
                      ? interval.end_at
                      : "--:--"
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="py-20 flex items-center justify-center" v-if="isRainSlotSaving">
          <Loader class="w-10 h-10" />
        </div>
      </div>

      <div
        class="px-4 py-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-end gap-2"
      >
        <button
          class="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold transition-all border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 active:scale-95 disabled:opacity-50"
          @click="toggleEditMode"
          :disabled="isRainSlotSaving"
        >
          <template v-if="!editMode">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
              />
            </svg>
            <span>Edit Schedule</span>
          </template>
          <template v-else>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>Cancel Edit</span>
          </template>
        </button>

        <button
          v-if="!editMode"
          @click="saveRains"
          :disabled="isRainSlotSaving"
          class="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold bg-[#386f99] text-white hover:bg-[#2d5a7d] shadow-lg shadow-blue-900/20 transition-all active:scale-95 disabled:opacity-50"
        >
          <i class="fa-solid fa-cloud-showers-heavy"></i>
          Generate Rain
        </button>
        <button
          v-else
          @click="saveChanges"
          :disabled="isRainSlotSaving || !isIntervalsValid"
          class="px-4 py-2.5 rounded-2xl flex items-center gap-2 text-sm font-bold bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-900/10 transition-all active:scale-95 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { postFetch } from "@/api/axiosClient";
 
import Swal from "sweetalert2";
import { CheckIcon } from "@heroicons/vue/24/outline";
import Loader from "./Loader.vue";
export default {
  name: "RainTimeModal",
  components: {
    Loader,
    CheckIcon,
  },
  computed: {
    isIntervalsValid() {
      return this.localIntervals.every((interval) => {
        return (
          interval.start_at &&
          interval.end_at &&
          !interval.start_at.includes("NaN") &&
          !interval.end_at.includes("NaN")
        );
      });
    },
  },
  props: {
    rainTimeGenerated: {
      type: Array,
      required: true,
      validator: function (value) {
        return value.every(
          (item) =>
            typeof item === "object" && "start_at" in item && "end_at" in item,
        );
      },
    },
    detail: {
      type: Object,
      required: true,
    },
    toggleModal: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      editMode: false,
      localIntervals: [],
      isRainSlotSaving: false,
      isRainNotGenerable: false,
      rainDuration: 0,
      isRandom: false,
    };
  },
  created() {
    this.localIntervals = JSON.parse(JSON.stringify(this.rainTimeGenerated));
  },
  async mounted() {
    document.body.style.overflow = "hidden";
    await this.checkIfCanGenerateRain();

    this.rainDuration = this.detail.duration;
  },
  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
  methods: {
    toggleEditMode() {
      if (this.editMode) {
        this.localIntervals = JSON.parse(
          JSON.stringify(this.rainTimeGenerated),
        );
      }
      this.editMode = !this.editMode;
    },
    validateTime(index) {
      const interval = this.localIntervals[index];

      if (!interval.start_at) return;

      const toMinutes = (time) => {
        if (!time || !time.includes(":")) return null;
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      const fromMinutes = (totalMinutes) => {
        const hours = Math.floor(totalMinutes / 60)
          .toString()
          .padStart(2, "0");
        const minutes = (totalMinutes % 60).toString().padStart(2, "0");
        return `${hours}:${minutes}`;
      };

      let startMinutes = toMinutes(interval.start_at);

      if (startMinutes === null || isNaN(startMinutes)) return;

      let endMinutes = startMinutes + this.rainDuration;

      if (endMinutes > 1439) {
        endMinutes = 1439;
        startMinutes = endMinutes - this.rainDuration;
        if (startMinutes < 0) startMinutes = 0;
      }

      interval.start_at = fromMinutes(startMinutes);
      interval.end_at = fromMinutes(endMinutes);
    },
    saveChanges() {
      this.$emit("update-intervals", this.localIntervals);
      this.editMode = false;
    },
    async saveRains() {
      try {
        this.isRainSlotSaving = true;

        const response = await postFetch(`freebets/rain`, {
          is_random: this.isRandom,
          times: this.localIntervals,
          campaign_id: this.detail._id,
          min_cashout: this.detail.min_cashout,
        });

        if (response?.message) {
          await Swal.fire({
            title: "Rain saved",
            text: `${response.count} rain slots have been created successfully.`,
          });

          this.switchToRainGeneratedTab();
        }
      } catch (error) {
        console.error("Error saving rain:", error);

        const errorMsg =
          error?.error || "An error occurred while saving rain slots.";

        await Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        this.isRainSlotSaving = false;
      }
    },
    async checkIfCanGenerateRain() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const campaignStart = new Date(this.detail.start_date);
      campaignStart.setHours(0, 0, 0, 0);

      if (campaignStart <= today) {
      }
    },
    async switchToRainGeneratedTab() {
      let rainTab = document.getElementById("tab-rains_generated");
      if (rainTab) {
        rainTab.click();
      }
    },
  },
};
</script>
